import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, RefreshCcw, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Header } from "@/components/Header";
import { AdminAuditModal } from "@/components/AdminAuditModal";

const Kpi = ({ label, value, sub }) => (
  <div className="impact-card p-6" data-testid={`kpi-${label}`}>
    <div className="eyebrow mb-2">{label}</div>
    <div className="font-display font-black text-3xl md:text-4xl tabular-nums">{value}</div>
    {sub && <div className="text-xs text-[color:var(--impact-ink-2)] mt-2">{sub}</div>}
  </div>
);

const LeadBadge = ({ q }) => {
  const cls = q === "hot" ? "priority-high" : q === "warm" ? "priority-medium" : "priority-low";
  return <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}>{q}</span>;
};

const AdminDashboardPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [quality, setQuality] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedAuditId, setSelectedAuditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/audits", { params: { q: q || undefined, quality: quality || undefined } }),
      ]);
      setStats(s.data);
      setRows(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("impact_admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("impact_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q, quality]);

  const signOut = () => {
    localStorage.removeItem("impact_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="eyebrow mb-2">Impact Audit AI</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl">{t("admin.dashboardTitle")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" className="btn-ghost">
              <RefreshCcw size={14} /> {loading ? "…" : ""}
            </button>
            <button onClick={signOut} data-testid="admin-signout-btn" className="btn-ghost">
              <LogOut size={14} /> {t("admin.signOut")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="kpi-grid">
          {stats && (
            <>
              <Kpi label={t("admin.kpi.total")} value={stats.total_audits} />
              <Kpi label={t("admin.kpi.new")} value={stats.new_leads_7d} />
              <Kpi label={t("admin.kpi.avg")} value={stats.average_score} sub="/ 100" />
              <Kpi label={t("admin.kpi.hot")} value={stats.high_potential_leads} />
              <Kpi label={t("admin.kpi.gaOpp")} value={stats.google_ads_opportunities} />
              <Kpi label={t("admin.kpi.webOpp")} value={stats.website_opportunities} />
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--impact-ink-2)]" />
            <input
              data-testid="admin-search-input"
              placeholder={t("admin.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[color:var(--impact-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--impact-ink)]/20"
            />
          </div>
          <select
            data-testid="admin-quality-filter"
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
            className="px-4 py-2.5 rounded-full border border-[color:var(--impact-border)] bg-white text-sm"
          >
            <option value="">{t("admin.quality.all")}</option>
            <option value="hot">{t("admin.quality.hot")}</option>
            <option value="warm">{t("admin.quality.warm")}</option>
            <option value="cold">{t("admin.quality.cold")}</option>
          </select>
        </div>

        <div className="impact-card overflow-hidden" data-testid="admin-audits-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--impact-bg)] text-left">
                <tr className="text-[color:var(--impact-ink-2)]">
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.date")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.company")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.contact")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.city")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.ads")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.score")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.lead")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider">{t("admin.table.services")}</th>
                  <th className="px-4 py-3 font-semibold uppercase text-[10px] tracking-wider text-right">{t("admin.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-14 text-[color:var(--impact-ink-2)]">
                      {t("admin.empty")}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-t border-[color:var(--impact-border)] hover:bg-[color:var(--impact-bg)]/60 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-[color:var(--impact-ink-2)]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.company_name}</div>
                      <a href={r.website_url} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--impact-brand)] animated-underline">
                        {r.website_url}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <div>{r.first_name} {r.last_name}</div>
                      <div className="text-xs text-[color:var(--impact-ink-2)]">{r.email}</div>
                      <div className="text-xs text-[color:var(--impact-ink-2)]">{r.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-[color:var(--impact-ink-2)]">{r.city}</td>
                    <td className="px-4 py-3 text-[color:var(--impact-ink-2)]">
                      {r.ads_status}{r.monthly_spend ? ` · ${r.monthly_spend}` : ""}
                    </td>
                    <td className="px-4 py-3 font-display font-bold tabular-nums">
                      {r.average_category_score === null || r.average_category_score === undefined
                        ? "—"
                        : r.average_category_score}
                    </td>
                    <td className="px-4 py-3"><LeadBadge q={r.lead_quality} /> <span className="text-xs text-[color:var(--impact-ink-2)] ml-1">{r.lead_score}</span></td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="text-xs text-[color:var(--impact-ink-2)] truncate">{(r.recommended_services || []).join(", ")}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedAuditId(r.id)}
                        data-testid={`admin-view-${r.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[color:var(--impact-border)] hover:bg-[color:var(--impact-bg)] transition-colors"
                        aria-label={t("admin.viewDetails")}
                      >
                        <Eye size={12} /> {t("admin.viewDetails")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      <AdminAuditModal
        auditId={selectedAuditId}
        open={!!selectedAuditId}
        onOpenChange={(o) => { if (!o) setSelectedAuditId(null); }}
      />
    </div>
  );
};

export default AdminDashboardPage;
